import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { useDefaultCamera } from "@/components/r3f/cameras/active-camera-context";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { useAnimateCameraToPredefinedViews } from "@/hooks/use-animate-camera-to-predefined-views";
import { useCenterCameraOnPointCloud } from "@/hooks/use-center-camera-on-pointcloud";
import { useManageProjectionSwitch } from "@/hooks/use-manage-projection-switch";
import { useCached3DObject } from "@/object-cache";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import {
  selectHiddenExportModeTabs,
  selectInitialExportTarget,
  selectSelectedExportModeTab,
  selectSelectedOrthophotoSide,
} from "@/store/modes/export-mode-selectors";
import { setSelectedOrthophotoSide } from "@/store/modes/export-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { toVec4Tuple } from "@/tools/clip-scene-tool";
import { useBoxControlsContext } from "@/utils/box-controls-context";
import {
  BoxControls,
  ExplorationControls,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { WalkOrbitControls } from "@faro-lotv/lotv";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Quaternion, Vector3 } from "three";
import { ExportModeRenderingPipeline } from "./export-mode-rendering-pipeline";
import { useComputeValidExportModeScene } from "./export-mode-state";
import { ExportModeTab, getTabsRequiringPointCloud } from "./export-mode-types";
import { ScansExportScene } from "./scans-export/scans-export-scene";
import { useFrameCameraIfNeeded } from "./use-frame-camera-if-needed";
import {
  BoxControlsViaRef,
  useManageClippingPlanesChange,
} from "./use-manage-clip-planes-change";

/** @returns the scene for the export mode */
export function ExportModeScene(): JSX.Element | null {
  useDefaultCamera();
  const hiddenTabs = useAppSelector(selectHiddenExportModeTabs);
  const selectedTab = useAppSelector(selectSelectedExportModeTab);

  // Check if all tabs that require a point cloud are hidden
  const areAllPointCloudTabsHidden = useMemo(() => {
    const tabsRequiringPointCloud = getTabsRequiringPointCloud();
    return tabsRequiringPointCloud.every((tab) => hiddenTabs.includes(tab));
  }, [hiddenTabs]);

  if (areAllPointCloudTabsHidden) {
    switch (selectedTab) {
      case ExportModeTab.scans:
        return <ScansExportScene />;
      default:
        throw new Error(`No scene implementation for tab: ${selectedTab}`);
    }
  }
  return <ExportModeSceneBase />;
}

function ExportModeSceneBase(): JSX.Element {
  const pointCloud = useComputeValidExportModeScene();
  assert(pointCloud, "Export Mode requires a point cloud as the main element");

  const dispatch = useAppDispatch();
  const selectedTab = useAppSelector(selectSelectedExportModeTab);
  const camera = useDefaultCamera();

  const pointCloudObject = useCached3DObject(pointCloud, "ExportModeScene");

  const {
    box: modelBB,
    cloudTransform,
    target: pointCloudCenterTarget,
    targetCameraPos,
  } = useCenterCameraOnPointCloud(pointCloudObject);

  const initialTarget = useAppSelector(selectInitialExportTarget);
  const selectedSide = useAppSelector(selectSelectedOrthophotoSide);

  const [target, setTarget] = useState(
    initialTarget ? new Vector3().fromArray(initialTarget) : undefined,
  );

  /** Position of the clipping box */
  const [boxPosition, setBoxPosition] = useState(
    modelBB.getCenter(new Vector3()),
  );

  /** Scale of the clipping box */
  const [boxScale, setBoxScale] = useState(
    modelBB.max.clone().sub(modelBB.min),
  );

  /** Rotation of the clipping box */
  const [boxRotation, setBoxRotation] = useState(new Quaternion());

  // Clipping box that the user may have saved in the store from previous interactions
  const clippingBox = useAppSelector(selectClippingBox);

  // If a clipping box is present in the store, use it
  useEffect(() => {
    if (!clippingBox) return;

    setBoxPosition(TEMP_POS.fromArray(clippingBox.position));
    setBoxScale(TEMP_SIZE.fromArray(clippingBox.size));
    setBoxRotation(new Quaternion().fromArray(clippingBox.rotation));
  }, [clippingBox]);

  const onAutoClipBox = useCallback(
    (obb: OrientedBoundingBox) => {
      // Update the current clipping box with the autoClippedBox
      // Creating a new vector is used to trigger properly the reactivity since the object reference would be otherwise the same
      setBoxPosition(new Vector3().fromArray(obb.position));
      setBoxScale(new Vector3().fromArray(obb.size));
      setBoxRotation(new Quaternion().fromArray(obb.rotation));
      dispatch(
        setClippingBox({
          position: obb.position,
          size: obb.size,
          rotation: obb.rotation,
        }),
      );
    },
    [dispatch],
  );

  const onResetClipBox = useCallback(() => {
    const newPos = modelBB.getCenter(new Vector3());
    const newScale = modelBB.max.clone().sub(modelBB.min);
    const newRotation = new Quaternion();
    setBoxPosition(newPos);
    setBoxScale(newScale);
    setBoxRotation(newRotation);
    dispatch(
      setClippingBox({
        position: newPos.toArray(),
        size: newScale.toArray(),
        rotation: toVec4Tuple(newRotation),
      }),
    );
  }, [modelBB, dispatch]);

  const [controlsEnabled, setControlsEnabled] = useState(true);

  const { hasUserInteracted, setHasUserInteracted } = useBoxControlsContext();

  // Reset the boolean to check if the user has interacted with the box controls on cleanup,
  // So that the help message can be shown on every entry to the export mode
  useEffect(
    () => () => {
      setHasUserInteracted(false);
    },
    [setHasUserInteracted],
  );

  const onInteractionStarted = useCallback(() => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }
    setControlsEnabled(false);
  }, [hasUserInteracted, setHasUserInteracted]);

  const [boxControls, setBoxControls] = useState<BoxControlsViaRef>(null);

  const onInteractionStopped = useCallback(() => {
    setControlsEnabled(true);
    if (!boxControls) return;
    dispatch(
      setClippingBox({
        position: boxControls.pos.toArray(),
        size: boxControls.size.toArray(),
        rotation: toVec4Tuple(boxControls.rot),
      }),
    );
  }, [boxControls, dispatch]);

  const interactiveObjects = useMemo(
    () => [pointCloudObject],
    [pointCloudObject],
  );

  // Updates the preview clipping box and calculates the local clipping planes for the clipping box state
  const { onClippingPlanesChanged, clippingPlanesPreview } =
    useManageClippingPlanesChange(
      modelBB,
      cloudTransform.worldMatrix,
      boxControls,
    );

  const {
    cameraAnimation,
    setCameraAnimation,
    setControlsForResetView,
    clippingBox: activeBox,
    setCameraView,
  } = useAnimateCameraToPredefinedViews(modelBB, setTarget);

  const { setControls: setControlsForCameraSwitch } = useManageProjectionSwitch(
    activeBox,
    setCameraView,
  );

  const setControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsForCameraSwitch(controls);
      setControlsForResetView(controls);
    },
    [setControlsForCameraSwitch, setControlsForResetView],
  );

  // If the mode was entered via a deep link, animate the camera to a position and rotation
  // where the whole point cloud is framed
  useFrameCameraIfNeeded(
    setCameraView,
    camera,
    targetCameraPos,
    pointCloudCenterTarget,
  );

  if (selectedTab === ExportModeTab.scans) {
    return <ScansExportScene />;
  }

  return (
    <>
      <PointCloudRenderer
        pointCloud={pointCloudObject}
        clippingPlanes={clippingPlanesPreview}
      />
      <BoxControls
        ref={setBoxControls}
        position={boxPosition}
        size={boxScale}
        rotation={boxRotation}
        clipInside={false}
        enableRotateX={false}
        enableRotateY={false}
        onInteractionStarted={onInteractionStarted}
        onInteractionStopped={onInteractionStopped}
        clippingPlanesChanged={onClippingPlanesChanged}
        enableSideSelection={selectedTab === ExportModeTab.orthophoto}
        selectedSide={selectedSide}
        onSideSelected={(side) => dispatch(setSelectedOrthophotoSide(side))}
        enabled={!cameraAnimation}
      />
      {!cameraAnimation && (
        <ExplorationControls
          ref={setControls}
          target={target}
          obstacles={interactiveObjects}
          enabled={controlsEnabled}
        />
      )}
      {cameraAnimation && (
        <CameraAnimation
          {...cameraAnimation}
          onAnimationFinished={() => setCameraAnimation(undefined)}
        />
      )}
      <ExportModeRenderingPipeline
        pointCloudObject={pointCloudObject}
        onAutoClipBox={onAutoClipBox}
        onResetClipBox={onResetClipBox}
      />
    </>
  );
}

const TEMP_POS = new Vector3();
const TEMP_SIZE = new Vector3();
